import { MAX_ROUNDS, Role } from "@gigsync/protocol";
import { conceded } from "./strategy.js";
/** Offers are whole ₹10s, as people quote prices. */
export const PRICE_STEP_PAISE = 1000;
/** The old fixed curve's offer by which a side has conceded all the way to its limit. */
const HORIZON = 4;
/** The old fixed curve: accept an acceptable offer this close to our own next one. */
const CLOSE_ENOUGH = 0.05;
const R = Role.enum;
/** The worker sells (higher is better for them); the consumer buys (lower is better). */
const better = (role, a, b) => (role === R.provider ? a >= b : a <= b);
/** The old fixed curve, kept for jobs whose negotiation began before strategies: eased to the limit by HORIZON. */
function fixedCurve(round) {
    const t = Math.min(Math.max(round - 1, 0) / HORIZON, 1);
    return 1 - (1 - t) ** 2;
}
/**
 * The price this side offers in its `round`-th offer (1 = opening), on the job's strategy when it has one. Rounded in
 * this side's favour, never below the legal minimum, never past the limit (for the household the limit wins: it never
 * offers more than its owner allowed).
 */
export function priceAt(role, m, round, s) {
    const covered = s ? conceded(s, round) : fixedCurve(round);
    const raw = m.opening_paise + (m.limit_paise - m.opening_paise) * covered;
    const steps = raw / PRICE_STEP_PAISE;
    const rounded = (role === R.provider ? Math.ceil(steps) : Math.floor(steps)) * PRICE_STEP_PAISE;
    const legal = Math.ceil((m.legal_paise ?? 0) / PRICE_STEP_PAISE) * PRICE_STEP_PAISE;
    const lawful = Math.max(rounded, legal);
    return better(role, lawful, m.limit_paise) ? lawful : m.limit_paise;
}
/** Within the owner's limit; for the worker, also not below the legal minimum. */
export const acceptable = (role, m, price) => better(role, price, m.limit_paise) && (role === R.requester || price >= (m.legal_paise ?? 0));
export const MoveKind = { accept: "accept", offer: "offer", walk: "walk" };
/**
 * This side's answer to the other side's offer, given how many offers it has made so far. Accepts an offer inside
 * the limit that is as good as, or close to (the strategy's closeness), what it would offer next; otherwise concedes
 * one step; walks away once its rounds are used up.
 */
export function respond(role, m, offersMade, theirPrice, s) {
    const round = offersMade + 1;
    const next = priceAt(role, m, round, s);
    if (acceptable(role, m, theirPrice)) {
        if (better(role, theirPrice, next))
            return { kind: MoveKind.accept };
        if (Math.abs(theirPrice - next) <= (s?.closeness ?? CLOSE_ENOUGH) * next)
            return { kind: MoveKind.accept };
    }
    if (offersMade >= MAX_ROUNDS)
        return { kind: MoveKind.walk };
    return { kind: MoveKind.offer, price_paise: next, round };
}
/** A sane mandate: positive prices, and the opening on the right side of the limit. */
export function validMandate(role, m) {
    return m.opening_paise > 0 && m.limit_paise > 0 && better(role, m.opening_paise, m.limit_paise);
}
//# sourceMappingURL=negotiate.js.map